import { isGoogleMaps } from '@/config/map';
import { fetchNearby, toMarker } from '@/shared/composables/useOsmNearby';
import MarkerMap from '@/shared/components/map/MarkerMap.vue';
import Vue from 'vue';
import MapCard from '@/Search/components/MapCard.vue';
import { defineComponent, onMounted, ref, watch, reactive, computed, watchEffect } from '@vue/composition-api';
// import { convertMapZoomToKilometers } from '@/Search/search.utils';
import useGoogleMap from '@/shared/composables/useGoogleMap';
const NEARBY_SEARCH_RADIUS = 500; // in meters
const DEFAULT_MAP_ZOOM = 8;
const NEARBY_MAP_ZOOM = 16;
export default defineComponent({
    components: {
        MapCard,
        MarkerMap
    },
    props: {
        venues: {
            type: Array,
            default: () => []
        },
        location: {
            type: Object,
            default: () => { }
        },
        highlightedMapCardVenueId: {
            type: Number,
            default: 0
        },
        isOrganisationFilterEnabled: {
            type: Boolean,
            default: false
        },
        isMapHidden: {
            type: Boolean,
            default: false
        },
        isNearbyModeEnabled: {
            type: Boolean,
            default: false
        }
    },
    setup(props, context) {
        const { root, emit } = context;
        const google = ref(null);
        const mapRef = ref(null);
        const map = ref(null);
        const isNearbyMode = ref(false);
        const zoomBeforeNearbyModeEntering = ref(DEFAULT_MAP_ZOOM);
        const isSearchWhileMoving = ref(false);
        const zoom = ref(DEFAULT_MAP_ZOOM);
        const currentMapCenter = reactive({ lat: null, lng: null });
        const mapOptions = reactive({
            zoomControl: true,
            zoomControlOptions: {
                position: null
            },
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUi: false,
            disableDoubleClickZoom: false
        });
        const selectedVenueId = ref(undefined);
        const nearbyFilters = reactive({
            transit_station: false,
            restaurant: false,
            cafe: false,
            parking: false
        });
        const nearbyList = reactive({
            restaurant: null,
            transit_station: null,
            cafe: null,
            parking: null
        });
        const placeMarkers = ref({});
        const selectedVenueMarker = ref(null);
        const cityCircle = ref(null);
        const placesService = computed(() => new google.value.maps.places.PlacesService(map.value));
        const serviceAnchor = computed(() => new google.value.maps.Point(7, 26));
        const labelAnchor = computed(() => new google.value.maps.Point(-17, 30));
        /**
         * Watch for nearby mode state changes. When nearby mode is enabled, set map options and zoom.
         */
        watch(() => isNearbyMode.value, () => {
            if (isNearbyMode.value) {
                // Save map zoom setting before entering the nearby mode
                // This value is then used to restore the map zoom on nearby mode exit
                zoomBeforeNearbyModeEntering.value = zoom.value;
            }
            zoom.value = isNearbyMode.value
                ? NEARBY_MAP_ZOOM
                : map.value?.zoom || DEFAULT_MAP_ZOOM;
            mapOptions.disableDoubleClickZoom = isNearbyMode.value;
            mapOptions.scaleControl = !isNearbyMode.value;
            mapOptions.zoomControl = !isNearbyMode.value;
        }, { immediate: true });
        /**
         * Watch for organisation filter state. When organisation filter is enabled,
         * disable search while moving.
         */
        watchEffect(() => {
            if (props.isOrganisationFilterEnabled) {
                isSearchWhileMoving.value = false;
            }
        });
        /**
         * Sets initial map center
         */
        watch(() => props.location, () => {
            if (props.location) {
                currentMapCenter.lng = props.location.lng;
                currentMapCenter.lat = props.location.lat;
            }
        }, { immediate: true });
        /**
         * Fits map bounds to the venues array so that all venues are visible on the map
         */
        const fitMapBounds = () => {
            if (!props.venues.length || !google.value)
                return;
            const coordinatesList = [];
            props.venues.forEach(venue => coordinatesList.push(new google.value.maps.LatLng(venue.latitude, venue.longitude)));
            //  Create a new viewpoint bound
            let bounds = new google.value.maps.LatLngBounds();
            //  Go through each...
            for (let i = 0, LtLgLen = coordinatesList.length; i < LtLgLen; i++) {
                //  And increase the bounds to take this point
                bounds.extend(coordinatesList[i]);
            }
            map.value.fitBounds(bounds);
        };
        const drawOverlays = () => {
            props.venues.forEach(venue => {
                const position = new google.value.maps.LatLng(venue.latitude, venue.longitude);
                const MapOverlay = getMapOverlay(google.value.maps.OverlayView);
                new MapOverlay(position, map.value, onPositionUpdated, venue.venueId);
            });
        };
        /**
         * Watch the length of venues array. When it changes, fit map bounds.
         * Works only if there is no lat/lng query params in the URL.
         * This is needed to adjust map zoom when user types new search request into the search header input.
         * When lat/lng query params are present in the URL, we don't want to change map zoom (search while moving).
         */
        watch(() => props.venues, (newVenues, oldVenues) => {
            if (newVenues.length !== oldVenues.length) {
                drawOverlays();
                if (!root.$route.query.lat) {
                    fitMapBounds();
                }
            }
        });
        /**
         * Watch for map visibility changes. When map is visible, fit map bounds.
         */
        watch(() => props.isMapHidden, () => {
            if (!props.isMapHidden) {
                fitMapBounds();
            }
        }, { immediate: true });
        /**
         * Watch for screen resolution changes. For mobile screens always hide the map initially.
         * This is needed to prevent map from being displayed on mobile screens when user opens the search page.
         * For the rest screen sizes, fit map bounds.
         */
        watch(() => root.$mq.phone, () => {
            if (!root.$mq.phone) {
                fitMapBounds();
            }
            else {
                emit('hideMap');
            }
        }, { immediate: true });
        const isMapMoving = ref(false);
        const mapStartedMoving = () => {
            isMapMoving.value = true;
        };
        /**
         * Watch for map movements by user. Updates current map center, puts lat/lng query params into the URL,
         * and removes search query param from the URL.
         */
        const mapEndedMoving = () => {
            isMapMoving.value = false;
            if (!isSearchWhileMoving.value)
                return;
            // if search while moving is enabled
            const center = map.value.getCenter();
            const newQuery = {
                ...root.$route.query,
                lat: center.lat(),
                lng: center.lng(),
                search: undefined
            };
            if (newQuery === root.$route.query) {
                return;
            }
            globalThis.$router.replace({
                query: newQuery
            });
        };
        const onMapZoomChanged = newZoom => {
            if (newZoom && newZoom !== DEFAULT_MAP_ZOOM) {
                // TODO: Uncomment if we want to use zoom value for distance calculation
                // on map zoom change event, set new map center
                // const currentMapCenterLat = currentMapCenter.lat;
                // ...and emit an event with the new view distance in kilometers
                // const viewDistance = convertMapZoomToKilometers(
                //   newZoom,
                //   currentMapCenterLat || 0
                // );
                // emit('currentZoomDistance', viewDistance);
                // ... and save new zoom into variable
                zoom.value = newZoom;
            }
        };
        const onClickMarker = (venueId) => {
            selectedVenueId.value = venueId;
            emit('mapMarkerClicked', venueId);
        };
        const setNearbyMode = (isNearbyModeEnabled) => {
            isNearbyMode.value = isNearbyModeEnabled;
            if (!isNearbyModeEnabled) {
                // remove all nearby markers from the map
                if (selectedVenueMarker.value) {
                    selectedVenueMarker.value.setMap(null);
                }
                Object.values(placeMarkers.value).forEach(markerGroup => markerGroup.forEach(marker => marker.setMap(null)));
                if (cityCircle.value) {
                    cityCircle.value.setMap(null);
                }
                // restore map zoom
                zoom.value = zoomBeforeNearbyModeEntering.value;
                emit('nearbyClosed');
            }
            else {
                // Everything below builds google.maps objects -- PlacesService,
                // Circle, MarkerWithLabel -- against `google.value`, which is null
                // under the osm provider because gmap-vue is not installed without a
                // key. The OSM path draws its own markers through the seam instead
                // (osmMarkers / loadOsmNearby), so this whole branch is Google's.
                if (!isGoogleMaps())
                    return;
                placeMarkers.value = {};
                const types = ['restaurant', 'cafe', 'parking', 'transit_station'];
                const requests = types.map(type => ({
                    location: {
                        lat: selectedVenue.value.latitude,
                        lng: selectedVenue.value.longitude
                    },
                    radius: NEARBY_SEARCH_RADIUS,
                    type: [type]
                }));
                // https://developers.google.com/maps/documentation/javascript/places?hl=nl#place_search_requests
                requests.forEach(request => placesService.value.nearbySearch(request, results => getNearbyPlaces(results, request.type[0])));
                if (cityCircle.value) {
                    cityCircle.value.setMap(null);
                }
                cityCircle.value = new google.value.maps.Circle({
                    strokeColor: '#2A1E5C',
                    strokeWeight: 1,
                    fillColor: '#ECE8F8',
                    fillOpacity: 0.3,
                    map: map.value,
                    center: {
                        lat: selectedVenue.value.latitude,
                        lng: selectedVenue.value.longitude
                    },
                    radius: NEARBY_SEARCH_RADIUS
                });
                const MarkerWithLabel = require('markerwithlabel')(google.value.maps);
                selectedVenueMarker.value = new MarkerWithLabel({
                    map: map.value,
                    icon: {
                        url: '/img/common/nearby-venue-marker.svg'
                    },
                    position: {
                        lat: selectedVenue.value.latitude,
                        lng: selectedVenue.value.longitude
                    },
                    labelContent: `<span>${selectedVenue.value.venueName}</span>`,
                    labelClass: 'nearby-venue-marker', // the CSS class for the label
                    labelAnchor: labelAnchor.value,
                    zIndex: 10000
                });
            }
        };
        /**
         * Watch for nearby mode switch from SelectedVenue.vue component.
         */
        watch(() => props.isNearbyModeEnabled, () => {
            if (props.isNearbyModeEnabled) {
                selectedVenueId.value = props.highlightedMapCardVenueId;
                setNearbyMode(true);
            }
        });
        const { getNearbyPlaces, onNearbyFilterClick, getMapOverlay } = useGoogleMap(nearbyFilters, placeMarkers, nearbyList, google, map, serviceAnchor, labelAnchor);
        const getFilteredNearbyList = computed(() => {
            let result = [];
            if (nearbyFilters.restaurant) {
                result = result.concat(nearbyList.restaurant);
            }
            if (nearbyFilters.cafe) {
                result = result.concat(nearbyList.cafe);
            }
            if (nearbyFilters.transit_station) {
                result = result.concat(nearbyList.transit_station);
            }
            if (nearbyFilters.parking) {
                result = result.concat(nearbyList.parking);
            }
            return result;
        });
        const selectedVenue = computed(() => {
            const index = props.venues.findIndex(venue => venue.venueId === selectedVenueId.value);
            if (index < 0) {
                setNearbyMode(false);
                return;
            }
            return props.venues[index];
        });
        /*
         * The same lookup, WITHOUT the side effect. Read this from templates.
         *
         * `selectedVenue` above is a computed that mutates: when nothing matches it
         * calls setNearbyMode(false), which emits `nearbyClosed`, which
         * SearchResults turns into `isMapVisible = false` on anything smaller than
         * desktop. So merely READING selectedVenue during a render closes the map
         * on mobile.
         *
         * That is why adding a `v-if="selectedVenue"` to the OSM popup slot broke
         * "Toon op kaart": the button emitted showMap, the map became visible, the
         * new render read selectedVenue, the computed emitted nearbyClosed, and the
         * map hid itself again in the same tick. Traced in Playwright 2026-09-14:
         *
         *   Map.emit showMap -> SR.showMap -> Map.emit nearbyClosed -> SR.onNearbyClosed
         *
         * Under Google this never surfaced because the only reads were inside the
         * `googleMapsEnabled` branch, which does not render under the osm provider.
         *
         * The impure computed is left alone deliberately: the Google nearby flow
         * depends on that side effect firing, and untangling it is a separate
         * change with its own risk. This gives templates a safe way to ask the same
         * question.
         */
        const selectedVenueForPopup = computed(() => props.venues.find(venue => venue.venueId === selectedVenueId.value));
        /**
         * Updates the position of the map card on the map or hides it if the position is outside the map bounds
         */
        const onPositionUpdated = (left, top, venueId, isPositionInsideMapBounds) => {
            const element = document.querySelector(`.map-card[data-venue="${venueId}"]`);
            if (element) {
                element.style.left = left;
                element.style.top = top;
                element.style.opacity =
                    isPositionInsideMapBounds && !isNearbyMode.value && !isMapMoving.value
                        ? 1
                        : 0;
            }
        };
        /**
         * Loads Google Maps API and sets default map options
         */
        onMounted(async () => {
            /*
             * Google only. Guarded, because under the osm provider gmap-vue is not
             * installed at all and Vue.$gmapApiPromiseLazy is undefined -- this threw
             * "Ht.default.$gmapApiPromiseLazy is not a function" on every mount, and
             * the rejection aborted the very render that reveals the map panel, so
             * pressing "Toon op kaart" on mobile did nothing at all. Measured on an
             * S25 over the DevTools protocol, 2026-09-14.
             *
             * Everything inside is Google-specific anyway: $mapPromise, $mapObject,
             * fitMapBounds via google.maps.LatLngBounds, and drawOverlays via
             * google.maps.OverlayView. The Leaflet branch fits its own bounds and
             * draws its own markers.
             */
            if (!isGoogleMaps())
                return;
            // https://diegoazh.github.io/gmap-vue/#the-three-main-utilities
            google.value = await Vue.$gmapApiPromiseLazy();
            await mapRef.value.$mapPromise;
            map.value = mapRef.value.$mapObject;
            fitMapBounds();
            drawOverlays();
        });
        /*
         * The venues as map markers, with the price drawn on the pin.
         *
         * Same formatting as MapCard (nl, EUR, no minor units) so the OSM path and
         * the Google path read identically rather than merely similarly.
         */
        const venueMarkers = computed(() => (props.venues || [])
            .filter((venue) => venue && venue.latitude && venue.longitude)
            .map((venue) => ({
            id: venue.venueId,
            position: { lat: venue.latitude, lng: venue.longitude },
            title: venue.venueName || String(venue.venueId),
            label: new Intl.NumberFormat('nl', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 0
            }).format(Number(venue.startingPrice || 0).toFixed(0))
        })));
        /*
         * "In de buurt", under the osm provider.
         *
         * The button flipped isNearbyMode and the watcher duly set zoom to 16, and
         * nothing happened: `fit-to-markers` was hardcoded on <MarkerMap>, so
         * Leaflet called fitBounds over every venue in the country on every render
         * and the zoom prop was never honoured. The reported symptom was a map of
         * the Netherlands and Belgium with a hundred price pills on it. The
         * template now passes `:fit-to-markers="!isNearbyMode"`, which is what
         * makes centre and zoom mean anything.
         *
         * Centre has to move too. currentMapCenter is only ever written from
         * props.location -- the SEARCH location -- so even once the zoom applied
         * it would have zoomed to 16 on the middle of the search area rather than
         * on the venue the user picked.
         */
        const osmNearbyPlaces = reactive({
            restaurant: [],
            cafe: [],
            parking: [],
            transit_station: []
        });
        const osmNearbyLoaded = reactive({
            restaurant: false,
            cafe: false,
            parking: false,
            transit_station: false
        });
        /*
         * selectedVenueForPopup, never selectedVenue. The latter is the computed
         * with the side effect -- reading it when nothing matches calls
         * setNearbyMode(false) and closes the map out from under the user. That
         * regression cost a day once already; see the comment on it below.
         */
        const osmCenter = computed(() => {
            const venue = selectedVenueForPopup.value;
            if (isNearbyMode.value && venue && venue.latitude && venue.longitude) {
                return { lat: venue.latitude, lng: venue.longitude };
            }
            return currentMapCenter;
        });
        const osmMarkers = computed(() => {
            if (!isNearbyMode.value)
                return venueMarkers.value;
            // In nearby mode the hundred other venues are noise: the question is
            // what is around THIS one. The venue keeps its price pill so it stays
            // recognisable as the thing that was selected.
            const venue = selectedVenueForPopup.value;
            const self = venue
                ? venueMarkers.value.filter(marker => marker.id === venue.venueId)
                : [];
            const active = Object.keys(osmNearbyPlaces).filter(type => nearbyFilters[type]);
            return self.concat(...active.map(type => osmNearbyPlaces[type] || []));
        });
        const loadOsmNearby = async (type) => {
            const venue = selectedVenueForPopup.value;
            if (osmNearbyLoaded[type] || !venue)
                return;
            osmNearbyLoaded[type] = true;
            const places = await fetchNearby(type, venue.latitude, venue.longitude);
            osmNearbyPlaces[type] = places.map(toMarker);
        };
        if (!isGoogleMaps()) {
            Object.keys(osmNearbyPlaces).forEach(type => {
                watch(() => nearbyFilters[type] && isNearbyMode.value, on => {
                    if (on)
                        loadOsmNearby(type);
                });
            });
            // A different venue means a different neighbourhood, so the cache has to
            // go with it. Without this, picking a second venue would draw the first
            // one's restaurants around it.
            watch(() => selectedVenueId.value, () => {
                Object.keys(osmNearbyPlaces).forEach(type => {
                    osmNearbyPlaces[type] = [];
                    osmNearbyLoaded[type] = false;
                });
            });
        }
        return {
            googleMapsEnabled: isGoogleMaps(),
            osmMarkers,
            osmCenter,
            selectedVenueId,
            selectedVenueForPopup,
            currentMapCenter,
            getFilteredNearbyList,
            isNearbyMode,
            isSearchWhileMoving,
            mapRef,
            mapOptions,
            nearbyFilters,
            selectedVenue,
            zoom,
            mapEndedMoving,
            mapStartedMoving,
            onClickMarker,
            setNearbyMode,
            onNearbyFilterClick,
            onMapZoomChanged
        };
    }
});
